/** Performance statistics and the STATS text. Point values are ASSUMPTIONS in the ATC-SIM spirit. */
export interface Stats {
  towerProfile?: boolean;
  runwayConflicts?: number;
  collisions: number;
  elapsedS: number;
  landings: number;
  handoffs: number;
  goArounds: number;
  safetyGoArounds: number;
  unstableApproaches: number;
  runwayGoArounds: number;
  /** Go-arounds because an ILS approach was never cleared to land. */
  noClearanceGoArounds: number;
  departureWaitS: number;
  missedIntercepts: number;
  abortedTakeoffs: number;
  /** Separation-loss episodes (a pair entering conflict counts once). */
  conflicts: number;
  conflictSeconds: number;
  /** Aircraft that left the airspace without a handoff or landing. */
  exits: number;
  /** Pilots who had to call a second time, press for an answer, or act on their own (sim/src/patience.ts). */
  secondCalls: number;
  urgentCalls: number;
  pilotActions: number;
  commands: number;
  rejectedCommands: number;
  /** Calls sent to another brain, refused by the checks, or not answered in time. */
  brainRequests: number;
  brainFallbacks: number;
  brainTimeouts: number;
  /** Crews that reported minimum fuel, declared a fuel emergency, or ran the tanks dry. */
  minimumFuelCalls: number;
  fuelEmergencies: number;
  forcedLandings: number;
  /** Emergencies declared on the frequency, and how many of those flights you landed. */
  emergencies: number;
  emergencyLandings: number;
  score: number;
}

export const POINTS = { landing: 10, handoff: 10, goAround: 0, unstableApproach: -5, runwayGoAround: -5, noClearanceGoAround: -5, missedIntercept: -5, conflict: -25, conflictSecond: -1, collision: -500, exit: -20, abortedTakeoff: 0, secondCall: -2, urgentCall: -5, pilotAction: -15, fuelEmergency: -20, forcedLanding: -100, emergencyLanding: 15 };

export function newStats(): Stats {
  return { elapsedS: 0, collisions: 0, landings: 0, handoffs: 0, goArounds: 0, safetyGoArounds: 0, unstableApproaches: 0, runwayGoArounds: 0, noClearanceGoArounds: 0, departureWaitS: 0, missedIntercepts: 0, abortedTakeoffs: 0, conflicts: 0, conflictSeconds: 0, exits: 0, secondCalls: 0, urgentCalls: 0, pilotActions: 0, commands: 0, rejectedCommands: 0, brainRequests: 0, brainFallbacks: 0, brainTimeouts: 0, minimumFuelCalls: 0, fuelEmergencies: 0, forcedLandings: 0, emergencies: 0, emergencyLandings: 0, score: 0 };
}

export function computeScore(s: Stats): number {
  return Math.round(
    -(s.runwayConflicts ?? 0) * 50 +
    s.landings * POINTS.landing + s.handoffs * POINTS.handoff + s.goArounds * POINTS.goAround + s.missedIntercepts * POINTS.missedIntercept +
    s.conflicts * POINTS.conflict + s.conflictSeconds * POINTS.conflictSecond + s.collisions * POINTS.collision + s.exits * POINTS.exit + s.abortedTakeoffs * POINTS.abortedTakeoff +
    s.unstableApproaches * POINTS.unstableApproach + s.runwayGoArounds * POINTS.runwayGoAround + s.noClearanceGoArounds * POINTS.noClearanceGoAround +
    s.secondCalls * POINTS.secondCall + s.urgentCalls * POINTS.urgentCall + s.pilotActions * POINTS.pilotAction +
    s.fuelEmergencies * (s.towerProfile ? 0 : POINTS.fuelEmergency) + s.forcedLandings * POINTS.forcedLanding + s.emergencyLandings * POINTS.emergencyLanding,
  );
}

export function statsText(s: Stats): string {
  const m = Math.floor(s.elapsedS / 60), sec = Math.floor(s.elapsedS % 60);
  return [
    `Time on position: ${m}:${String(sec).padStart(2, '0')}`,
    `Landings: ${s.landings}    Handoffs: ${s.handoffs}`,
    `Separation losses: ${s.conflicts} (${Math.round(s.conflictSeconds)} s)`,
    `Aircraft collisions: ${s.collisions}`,
    `Go-arounds: ${s.goArounds}    Missed intercepts: ${s.missedIntercepts}    Aborted take-offs: ${s.abortedTakeoffs}`,
    `Left airspace unhandled: ${s.exits}`,
    `Pilots kept waiting: ${s.secondCalls} second calls, ${s.urgentCalls} pressing, ${s.pilotActions} acted alone`,
    `Commands: ${s.commands} (${s.rejectedCommands} rejected)`,
    `Score: ${s.score}`,
  ].join('\n');
}
